import { useCallback, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";

import { GlobalFailedPaymentBanner } from "@/components/billing/failed-payment-banner";
import { BrandAssistantDrawer } from "@/components/chat/brand-assistant-widget";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useReleaseAccessGate } from "@/hooks/use-release-access-gate";
import { buildBrandPath, useRouteBrand } from "@/hooks/use-route-brand";

const ASSISTANT_DRAWER_OPEN_COOKIE = "cheetah_assistant_drawer_open";
const ASSISTANT_DRAWER_OPEN_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
const MAIN_SIDEBAR_COLLAPSED_COOKIE = "cheetah_main_sidebar_collapsed";
const MAIN_SIDEBAR_COLLAPSED_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

const getInitialAssistantDrawerOpen = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim() === `${ASSISTANT_DRAWER_OPEN_COOKIE}=1`);
};

const setAssistantDrawerOpenCookie = (open: boolean) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${ASSISTANT_DRAWER_OPEN_COOKIE}=${open ? "1" : "0"}; path=/; max-age=${ASSISTANT_DRAWER_OPEN_MAX_AGE_SECONDS}; samesite=lax`;
};

const getInitialMainSidebarCollapsed = (): boolean => {
  if (typeof document === "undefined") {
    return true;
  }

  const sidebarCookie = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${MAIN_SIDEBAR_COLLAPSED_COOKIE}=`));

  if (!sidebarCookie) {
    return true;
  }

  return sidebarCookie === `${MAIN_SIDEBAR_COLLAPSED_COOKIE}=1`;
};

const setMainSidebarCollapsedCookie = (collapsed: boolean) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${MAIN_SIDEBAR_COLLAPSED_COOKIE}=${collapsed ? "1" : "0"}; path=/; max-age=${MAIN_SIDEBAR_COLLAPSED_MAX_AGE_SECONDS}; samesite=lax`;
};

/**
 * Layout for the main dashboard — sidebar + content.
 * Used by Brand Pulse and all future dashboard routes.
 */
export default function DashboardLayout() {
  const { brandId } = useParams<{ brandId: string }>();
  const location = useLocation();
  const [assistantOpen, setAssistantOpenState] = useState(getInitialAssistantDrawerOpen);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(getInitialMainSidebarCollapsed);
  const { user, isPending } = useAuthenticatedUser();
  const { brand } = useRouteBrand();
  const releaseAccessGate = useReleaseAccessGate({
    enabled: Boolean(user),
    accessRequired: true,
  });
  const pulsePath = brandId ? buildBrandPath(brandId, "/pulse") : null;
  const isPulseRoute = Boolean(
    pulsePath && (location.pathname === pulsePath || location.pathname.startsWith(`${pulsePath}/`)),
  );
  const showAssistantAction = Boolean(brandId && brand && !isPulseRoute);
  const setAssistantOpen = useCallback((open: boolean) => {
    setAssistantOpenState(open);
    setAssistantDrawerOpenCookie(open);
  }, []);
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    setMainSidebarCollapsedCookie(collapsed);
  }, []);

  if (isPending || releaseAccessGate.isPending) {
    return <div className="h-screen bg-background" />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (releaseAccessGate.redirectTo) {
    return <Navigate to={releaseAccessGate.redirectTo} replace />;
  }

  return (
    <div className="flex h-screen bg-background">
      <AppSidebar
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        showAssistantAction={showAssistantAction}
        onOpenAssistant={() => setAssistantOpen(true)}
      />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <GlobalFailedPaymentBanner brandId={brandId} />
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-y-auto">
            <Outlet />
          </div>
          <BrandAssistantDrawer open={assistantOpen} onOpenChange={setAssistantOpen} />
        </div>
      </main>
    </div>
  );
}
